import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Star, X } from 'lucide-react';
import { submitFeedback } from '../api/insights';
import { useTheme } from '../context/ThemeContext';
import { toast } from 'sonner';

const RATING_MODAL_COOLDOWN = 6 * 60 * 1000; // 3 minutes
export const FEEDBACK_STORAGE_KEY = 'insights_rating_seen';
export const FEEDBACK_SUBMITTED_KEY = 'insights_feedback_submitted';
const SESSION_START_KEY = 'insights_session_start';
export const OPEN_FEEDBACK_MODAL_EVENT = 'insights-open-feedback-modal';
export const FEEDBACK_COMPLETED_EVENT = 'insights-feedback-completed';

export default function RatingModal() {
    const { isDark } = useTheme();
    const [isOpen, setIsOpen] = useState(false);
    const [rating, setRating] = useState(0);
    const [hoveredRating, setHoveredRating] = useState(0);
    const [comment, setComment] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [step, setStep] = useState('rating'); // 'rating' or 'thankyou'

    useEffect(() => {
        const hasSeen = localStorage.getItem(FEEDBACK_STORAGE_KEY);
        if (hasSeen) return;

        const checkTime = () => {
            // ONLY start the timer if the user has clicked "Track Alumni Network"
            const trackingStarted = sessionStorage.getItem('insights_tracking_started');
            if (!trackingStarted) return;

            let sessionStart = sessionStorage.getItem(SESSION_START_KEY);
            if (!sessionStart) {
                sessionStart = Date.now().toString();
                sessionStorage.setItem(SESSION_START_KEY, sessionStart);
            }

            const elapsed = Date.now() - parseInt(sessionStart, 10);
            if (elapsed >= RATING_MODAL_COOLDOWN) {
                setIsOpen(true);
                clearInterval(timer);
            }
        };

        const timer = setInterval(checkTime, 5000); // Check every 5 seconds
        checkTime(); // Initial check

        return () => clearInterval(timer);
    }, []);

    useEffect(() => {
        const openFromHeader = () => {
            setStep('rating');
            setIsOpen(true);
        };
        window.addEventListener(OPEN_FEEDBACK_MODAL_EVENT, openFromHeader);
        return () => window.removeEventListener(OPEN_FEEDBACK_MODAL_EVENT, openFromHeader);
    }, []);

    const handleSubmit = async () => {
        if (rating === 0) {
            toast.error('Please select a rating');
            return;
        }

        setIsSubmitting(true);
        try {
            await submitFeedback(rating, comment);
            localStorage.setItem(FEEDBACK_STORAGE_KEY, 'true');
            localStorage.setItem(FEEDBACK_SUBMITTED_KEY, 'true');
            window.dispatchEvent(new CustomEvent(FEEDBACK_COMPLETED_EVENT));
            setStep('thankyou');
            setTimeout(() => {
                setIsOpen(false);
            }, 3000);
        } catch (err) {
            toast.error('Failed to submit feedback');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDismiss = () => {
        setIsOpen(false);
        localStorage.setItem(FEEDBACK_STORAGE_KEY, 'true');
    };

    if (!isOpen) return null;

    return (
        <AnimatePresence>
            <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
                {/* Backdrop */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={handleDismiss}
                    className="absolute inset-0 backdrop-blur-md"
                    style={{ background: isDark ? 'rgba(5,4,3,0.75)' : 'rgba(241,245,249,0.8)' }}
                />

                {/* Modal Container */}
                <motion.div
                    initial={{ opacity: 0, scale: 0.9, y: 20 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.9, y: 20 }}
                    className="relative w-full max-w-md overflow-hidden rounded-3xl shadow-2xl"
                    style={{
                        background: isDark ? '#0d1019' : '#ffffff',
                        border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(37,99,235,0.15)'}`
                    }}
                >
                    {/* Subtle amber glow top */}
                    <div className="absolute top-0 left-1/2 -translate-x-1/2 w-64 h-32 pointer-events-none"
                        style={{ background: 'radial-gradient(ellipse at center, rgba(245,158,11,0.12) 0%, transparent 70%)' }} />

                    {/* Close Button */}
                    <button
                        onClick={handleDismiss}
                        className="absolute right-5 top-5 p-2 rounded-full transition-all"
                        style={{
                            color: isDark ? 'rgba(255,255,255,0.4)' : '#64748b',
                            background: isDark ? 'rgba(255,255,255,0.05)' : '#f1f5f9'
                        }}
                    >
                        <X className="w-4 h-4" />
                    </button>

                    <div className="p-8 sm:p-10 pt-10 sm:pt-12">
                        <AnimatePresence mode="wait">
                            {step === 'rating' ? (
                                <motion.div
                                    key="rating-step"
                                    initial={{ opacity: 0, x: 20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    exit={{ opacity: 0, x: -20 }}
                                    className="space-y-6"
                                >
                                    <div className="text-center space-y-3">
                                        <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-2"
                                            style={{
                                                background: '#fff',
                                                border: '1px solid rgba(255,255,255,0.1)',
                                                padding: '8px'
                                            }}>
                                            <img src="/assets/logo.png" alt="logo" className="w-full h-full object-contain" />
                                        </div>
                                        <h2 className="text-2xl font-bold" style={{ color: isDark ? '#ffffff' : '#0f172a' }}>Enjoying fairhire?</h2>
                                        <p className="text-base leading-relaxed" style={{ color: isDark ? 'rgba(255,255,255,0.6)' : '#475569' }}>
                                            Your feedback helps us improve the experience for everyone.
                                        </p>
                                    </div>

                                    <div className="flex justify-center gap-2 py-4">
                                        {[1, 2, 3, 4, 5].map((star) => (
                                            <button
                                                key={star}
                                                onMouseEnter={() => setHoveredRating(star)}
                                                onMouseLeave={() => setHoveredRating(0)}
                                                onClick={() => setRating(star)}
                                                className="transition-all duration-200 transform hover:scale-110 focus:outline-none"
                                            >
                                                <Star
                                                    size={40}
                                                    className="transition-colors duration-200"
                                                    style={{
                                                        fill: (hoveredRating || rating) >= star ? '#F59E0B' : 'transparent',
                                                        color: (hoveredRating || rating) >= star ? '#F59E0B' : (isDark ? 'rgba(255,255,255,0.15)' : '#e2e8f0')
                                                    }}
                                                />
                                            </button>
                                        ))}
                                    </div>

                                    <AnimatePresence>
                                        {rating > 0 && (
                                            <motion.div
                                                initial={{ opacity: 0, height: 0 }}
                                                animate={{ opacity: 1, height: 'auto' }}
                                                className="space-y-4 pt-2"
                                            >
                                                <textarea
                                                    placeholder="Any thoughts? Good or bad, we're listening. (Optional)"
                                                    value={comment}
                                                    onChange={(e) => setComment(e.target.value)}
                                                    className="w-full rounded-2xl p-4 transition-all resize-none outline-none"
                                                    style={{
                                                        background: isDark ? 'rgba(255,255,255,0.05)' : '#f8fafc',
                                                        border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : '#e2e8f0'}`,
                                                        color: isDark ? '#ffffff' : '#1e293b'
                                                    }}
                                                    rows={3}
                                                />
                                                <button
                                                    onClick={handleSubmit}
                                                    disabled={isSubmitting}
                                                    className="w-full font-bold py-4 rounded-2xl transition-all shadow-lg disabled:opacity-50"
                                                    style={{
                                                        background: isDark ? '#F59E0B' : '#2563eb',
                                                        color: isDark ? '#1a1612' : '#ffffff',
                                                        boxShadow: isDark ? '0 10px 15px -3px rgba(245,158,11,0.2)' : '0 10px 15px -3px rgba(37,99,235,0.2)'
                                                    }}
                                                >
                                                    {isSubmitting ? 'Submitting...' : 'Submit Feedback'}
                                                </button>
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </motion.div>
                            ) : (
                                <motion.div
                                    key="thankyou-step"
                                    initial={{ opacity: 0, scale: 0.9 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    className="py-10 flex flex-col items-center text-center space-y-6"
                                >
                                    <div className="w-20 h-20 rounded-full flex items-center justify-center mb-2"
                                        style={{ background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.2)' }}>
                                        <Star className="text-[#F59E0B] fill-[#F59E0B]" size={40} />
                                    </div>
                                    <div className="space-y-2">
                                        <h3 className="text-3xl font-bold" style={{ color: isDark ? '#ffffff' : '#0f172a' }}>Thank You!</h3>
                                        <p className="text-lg" style={{ color: isDark ? 'rgba(255,255,255,0.6)' : '#475569' }}>
                                            We appreciate your Feedback.
                                        </p>
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                </motion.div>
            </div>
        </AnimatePresence>
    );
}
